import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { saveEditorPage, deleteSiteSub, getSiteSubs } from '../../services/CommonServices';
import { useBuilder } from '../../contexts/BuilderContext';

// --- Initial Data ---
const initialSiteSubTemplates = [
  {
    id: 'siteSub.header-01',
    type: 'siteSub.header-01',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.header-02',
    type: 'siteSub.header-02',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.html',
    type: 'siteSub.html',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.properties-01',
    type: 'siteSub.properties-01',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.properties-02',
    type: 'siteSub.properties-02',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.contactus',
    type: 'siteSub.contactus',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.hero-contact',
    type: 'siteSub.hero-contact',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.virtualshowing-01',
    type: 'siteSub.virtualshowing-01',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.footer-01',
    type: 'siteSub.footer-01',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.footer-02',
    type: 'siteSub.footer-02',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.grid-01',
    type: 'siteSub.grid-01',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.grid-02',
    type: 'siteSub.grid-02',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.list-2column-01',
    type: 'siteSub.list-2column-01',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.onepage-menu-01',
    type: 'siteSub.onepage-menu-01',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.left-fixed-menu-01',
    type: 'siteSub.left-fixed-menu-01',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.blog-preview',
    type: 'siteSub.blog-preview',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.blog-tab',
    type: 'siteSub.blog-tab',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
  {
    id: 'siteSub.image-text-split',
    type: 'siteSub.image-text-split',
    preview: 'https://i.ibb.co/VSGsh4p/header-01.png',
  },
];

// --- Styled Components ---

const EditorContainer = styled.div`
  padding: 0;
  color: #fff;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #444;
`;

const Title = styled.h5`
  margin: 0;
  font-weight: 600;
  color: #eee;
`;

const SubTitle = styled.h6`
  margin: 10px 0;
  font-size: 0.9rem;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Card = styled.div`
  background-color: ${(props) => (props.isDragging ? '#444' : '#2a2a2a')};
  border: 1px solid #444;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  box-shadow: ${(props) =>
    props.isDragging ? '0 5px 15px rgba(0,0,0,0.3)' : 'none'};
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #666;
  }
`;

const DragHandle = styled.div`
  margin-right: 15px;
  cursor: grab;
  color: #888;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #fff;
  }
  
  &::before {
    content: '☰'; 
    font-size: 1.2rem;
  }
`;

const Content = styled.div`
  flex-grow: 1;
  font-size: 0.95rem;
  font-weight: 500;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: 1px solid #d9534f;
  color: #d9534f;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #d9534f;
    color: white;
  }
`;

const SaveButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 20px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #218838;
  }

  &:disabled {
    background-color: #555;
    cursor: not-allowed;
  }
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-top: 15px;
  padding-right: 5px;
`;

const TemplateItem = styled.div`
  background-color: #222;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 10px;
  cursor: grab;
  transition: transform 0.2s, border-color 0.2s;

  &:hover {
    transform: translateY(-2px);
    border-color: #777;
  }
  
  img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    margin-top: 8px;
    opacity: 0.8;
  }

  span {
    display: block;
    font-size: 0.85rem;
    color: #ccc;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const DropZone = styled.div`
  min-height: 200px;
  background-color: ${(props) =>
    props.isDraggingOver ? 'rgba(40, 167, 69, 0.1)' : 'transparent'};
  border: 2px dashed ${(props) => (props.isDraggingOver ? '#28a745' : '#444')};
  border-radius: 8px;
  padding: 15px;
  transition: all 0.2s ease;
`;

// --- Global Layout Section Styled Components ---
const GlobalLayoutSection = styled.div`
  background-color: #1e1e1e;
  border: 1px solid #28a745;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
`;

const GlobalLayoutTitle = styled.h6`
  color: #28a745;
  margin-bottom: 15px;
  font-weight: 600;
  font-size: 0.95rem;
`;

const SelectRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  label {
    width: 80px;
    color: #aaa;
    font-size: 0.9rem;
    margin: 0;
  }

  select {
    flex: 1;
    background-color: #333;
    color: white;
    border: 1px solid #555;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.9rem;

    &:focus {
      outline: none;
      border-color: #28a745;
    }
  }
`;

const ApplyLayoutButton = styled.button`
  width: 100%;
  padding: 10px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #218838;
  }

  &:disabled {
    background-color: #555;
    cursor: not-allowed;
  }
`;

const EditorPage = (props) => {
  const { intl, pageId, site, onGlobalLayoutSave } = props;
  const { updateLiveSiteSubs } = useBuilder();

  // 헤더/푸터가 아닌 컨텐츠 템플릿만 필터링
  const contentTemplates = initialSiteSubTemplates.filter(
    (t) => !t.type.includes('header') && !t.type.includes('footer')
  );

  // 현재 siteSubs에서 헤더/푸터 제외
  const filterHeaderFooter = (subs) =>
    (subs || []).filter(
      (sub) => !sub.type.includes('header') && !sub.type.includes('footer')
    );

  // State
  const [currentSiteSubs, setCurrentSiteSubs] = useState(filterHeaderFooter(props.siteSubs));
  const [siteSubTemplates] = useState(contentTemplates);
  const [deletedSubIds, setDeletedSubIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Global Layout State
  const [selectedHeader, setSelectedHeader] = useState(
    site?.globalHeaderType || 'siteSub.header-01'
  );
  const [selectedFooter, setSelectedFooter] = useState(
    site?.globalFooterType || 'siteSub.footer-01'
  );
  const [isSavingLayout, setIsSavingLayout] = useState(false);

  // Update local state if props change (e.g. switching pages)
  React.useEffect(() => {
    setCurrentSiteSubs(filterHeaderFooter(props.siteSubs));
    setDeletedSubIds([]);
  }, [props.siteSubs]);

  // Update global layout state when site changes
  React.useEffect(() => {
    if (site) {
      setSelectedHeader(site.globalHeaderType || 'siteSub.header-01');
      setSelectedFooter(site.globalFooterType || 'siteSub.footer-01');
    }
  }, [site]);

  // Global Layout Save Handler
  const handleSaveGlobalLayout = async () => {
    if (!site?.id || !onGlobalLayoutSave) return;
    setIsSavingLayout(true);
    try {
      await onGlobalLayoutSave(site.id, selectedHeader, selectedFooter);
    } catch (error) {
      console.error('Error saving global layout:', error);
      alert('Error saving layout: ' + error.message);
    } finally {
      setIsSavingLayout(false);
    }
  };

  // --- Handlers ---

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId && destination.droppableId === 'currentSiteSubs') {
      // Reordering
      const items = Array.from(currentSiteSubs);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      setCurrentSiteSubs(items);
    } else if (source.droppableId === 'siteSubTemplates' && destination.droppableId === 'currentSiteSubs') {
      // Adding from templates
      const itemToAdd = {
        ...siteSubTemplates[source.index],
        id: `template-${Date.now()}-${Math.random()}`, // Temporary ID
        order: destination.index, // Initial order, will be recalculated on save
      };
      
      const newItems = [...currentSiteSubs];
      newItems.splice(destination.index, 0, itemToAdd);
      setCurrentSiteSubs(newItems);
    }
  };

  const handleDelete = (index, itemId) => {
    if (!window.confirm(intl.formatMessage({ id: 'confirm_delete', defaultMessage: 'Are you sure you want to delete this item?' }))) {
      return;
    }

    // If it's an existing DB item (not a template), mark it for deletion
    if (!itemId.toString().startsWith('template-')) {
      setDeletedSubIds((prev) => [...prev, itemId]);
    }

    // Remove from UI immediately
    setCurrentSiteSubs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // 1. Process Deletions
      if (deletedSubIds.length > 0) {
        await Promise.all(deletedSubIds.map(id => deleteSiteSub(id)));
      }

      // 2. Prepare Ordered Data
      const orderedSiteSubs = currentSiteSubs.map((sub, index) => ({
        ...sub,
        order: index,
      }));

      // 3. Save Updates/Inserts
      const response = await saveEditorPage(pageId, orderedSiteSubs);

      if (response.data.success) {
        setDeletedSubIds([]); // Clear deleted queue

        // 4. Fetch fresh data from server and update live preview
        try {
          const freshDataResponse = await getSiteSubs(pageId);
          const freshSiteSubs = freshDataResponse.data.data || [];

          // Update live preview in context (no page reload needed)
          updateLiveSiteSubs(freshSiteSubs);

          // Also update local state with fresh data (to get new IDs for new items)
          setCurrentSiteSubs(filterHeaderFooter(freshSiteSubs));

          alert('Site structure saved successfully!');
        } catch (fetchError) {
          console.error('Error fetching fresh data:', fetchError);
          // Fallback to reload if fetch fails
          window.location.reload();
        }
      } else {
        throw new Error(response.data.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving data: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditorContainer>
      <DragDropContext onDragEnd={onDragEnd}>

        {/* Global Layout Selection */}
        <GlobalLayoutSection>
          <GlobalLayoutTitle>
            {intl.formatMessage({ id: 'editor.global_layout', defaultMessage: '전체 레이아웃' })}
          </GlobalLayoutTitle>
          <SelectRow>
            <label>{intl.formatMessage({ id: 'editor.header', defaultMessage: '헤더' })}</label>
            <select
              value={selectedHeader}
              onChange={(e) => setSelectedHeader(e.target.value)}
            >
              <option value="siteSub.header-01">
                {intl.formatMessage({ id: 'siteSub.header-01.title', defaultMessage: 'Header Type 01' })}
              </option>
              <option value="siteSub.header-02">
                {intl.formatMessage({ id: 'siteSub.header-02.title', defaultMessage: 'Header Type 02' })}
              </option>
            </select>
          </SelectRow>
          <SelectRow>
            <label>{intl.formatMessage({ id: 'editor.footer', defaultMessage: '푸터' })}</label>
            <select
              value={selectedFooter}
              onChange={(e) => setSelectedFooter(e.target.value)}
            >
              <option value="siteSub.footer-01">
                {intl.formatMessage({ id: 'siteSub.footer-01.title', defaultMessage: 'Footer Type 01' })}
              </option>
              <option value="siteSub.footer-02">
                {intl.formatMessage({ id: 'siteSub.footer-02.title', defaultMessage: 'Footer Type 02' })}
              </option>
            </select>
          </SelectRow>
          <ApplyLayoutButton onClick={handleSaveGlobalLayout} disabled={isSavingLayout}>
            {isSavingLayout
              ? intl.formatMessage({ id: 'common.saving', defaultMessage: 'Saving...' })
              : intl.formatMessage({ id: 'editor.apply_layout', defaultMessage: '레이아웃 적용' })}
          </ApplyLayoutButton>
        </GlobalLayoutSection>

        {/* Header / Toolbar */}
        <SectionHeader>
          <Title>{intl.formatMessage({ id: 'editor.page_content', defaultMessage: 'Page Content' })}</Title>
          <SaveButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </SaveButton>
        </SectionHeader>

        {/* Main Content: Droppable List */}
        <Droppable droppableId="currentSiteSubs">
          {(provided, snapshot) => (
            <DropZone
              ref={provided.innerRef}
              {...provided.droppableProps}
              isDraggingOver={snapshot.isDraggingOver}
            >
              {currentSiteSubs.length === 0 && (
                <div className="text-center text-muted py-4">
                  Drag templates here to build your page.
                </div>
              )}
              
              {currentSiteSubs.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id.toString()}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      isDragging={snapshot.isDragging}
                      style={{ ...provided.draggableProps.style }}
                    >
                      <DragHandle {...provided.dragHandleProps} title="Drag to reorder" />
                      <Content>
                         {intl.formatMessage({ id: `${item.type}.title`, defaultMessage: item.type })}
                      </Content>
                      <DeleteButton onClick={() => handleDelete(index, item.id)}>
                        Delete
                      </DeleteButton>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </DropZone>
          )}
        </Droppable>

        {/* Templates Section */}
        <div className="mt-4 pt-3 border-top border-secondary">
          <SubTitle>Available Components</SubTitle>
          <p className="text-muted small mb-2">Drag these items into the list above.</p>
          
          <Droppable droppableId="siteSubTemplates" isDropDisabled={true}>
            {(provided, snapshot) => (
              <TemplateGrid
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {siteSubTemplates.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <React.Fragment>
                        <TemplateItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{ ...provided.draggableProps.style }}
                        >
                           <span>
                             {intl.formatMessage({ id: `${item.type}.title`, defaultMessage: item.type })}
                           </span>
                           <img src={item.preview} alt="preview" />
                        </TemplateItem>
                        {snapshot.isDragging && (
                          <TemplateItem style={{opacity: 0.5}}>
                             <span>
                               {intl.formatMessage({ id: `${item.type}.title`, defaultMessage: item.type })}
                             </span>
                             <img src={item.preview} alt="preview" />
                          </TemplateItem>
                        )}
                      </React.Fragment>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TemplateGrid>
            )}
          </Droppable>
        </div>

      </DragDropContext>
    </EditorContainer>
  );
};

export default injectIntl(EditorPage);
